import * as THREE from 'three';
import type { PublicPartyConfig } from '../../shared/config.ts';
import type { Wish, AvatarAppearance } from '../../shared/protocol.ts';
import { ROOM_OBSTACLES } from '../../shared/layout.ts';

/** Shared unit geometry and one instanced material keep the miniature inexpensive. */
export const cube = new THREE.BoxGeometry(1, 1, 1);
export const matte = new THREE.MeshLambertMaterial({ color: 0xffffff });
const glow = new THREE.MeshBasicMaterial({color:0xffe9b5,toneMapped:false});
type Block = { section: string; x: number; y: number; z: number; w: number; h: number; d: number; color: THREE.ColorRepresentation; rx: number; ry: number; rz: number };

export class Blocks {
  section = 'room';
  plants = true;
  items: Block[] = [];
  box(x: number, y: number, z: number, w: number, h: number, d: number, color: THREE.ColorRepresentation, rx = 0, ry = 0, rz = 0) {
    this.items.push({ section: this.section, x, y, z, w, h, d, color, rx, ry, rz });
    return this;
  }
  build(parent: THREE.Object3D, material: THREE.Material = matte, _shadow = false) {
    const mesh = new THREE.InstancedMesh(cube, material, this.items.length);
    const transform = new THREE.Object3D();
    const color = new THREE.Color();
    this.items.forEach((input, i) => {
      const b = remap(input);
      transform.position.set(b.x, b.y, b.z);
      transform.scale.set(b.w, b.h, b.d);
      transform.rotation.set(b.rx, b.ry, b.rz);
      transform.updateMatrix();
      mesh.setMatrixAt(i, transform.matrix);
      mesh.setColorAt(i, color.set(b.color));
    });
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.computeBoundingSphere();
    mesh.updateMatrix(); mesh.matrixAutoUpdate=false;
    parent.add(mesh);
    return mesh;
  }
}

function remap(b: Block): Block {
  let sx=1,sz=1,tx=0,tz=0;
  switch(b.section) {
    case 'architecture': sx=1.1;sz=1.1;break;
    case 'kitchen': sx=.94;sz=.95;tx=-.952;tz=-.5415;break;
    case 'sofa': sx=.8;sz=.96;tx=-1.79;tz=-.2108;break;
    case 'side-table': sx=.91;sz=.91;tx=-2.8754;tz=2.3039;break;
    case 'rug':sx=1.25;sz=1.86;tx=-.125;tz=-2.976;break;
    case 'cake-table':sx=.95;sz=.65;tz=-3.353;break;
    case 'gift-table':sx=.96;sz=.8;tx=.6864;tz=-1.604;break;
    case 'plants':tx=b.x>0?.36:-.41;tz=b.x>0?.99:.44;break;
    case 'train':tx=.31;tz=1.34;break;
    case 'blocks':tx=-7.92;tz=2.42;break;
    case 'weights':tx=b.x>0?.18:-1.74;tz=b.x>0?.45:-.35;break;
  }
  return {...b,x:b.x*sx+tx,z:b.z*sz+tz,w:b.w*sx,d:b.d*sz};
}

const P = {
  ivory: '#fff6e9', wall: '#f2e8df', trim: '#fffaf1', wood: '#c58f65', darkWood: '#956548',
  pink: '#edb3bf', peach: '#f6bd98', orange: '#e88d50', lavender: '#a69cce', lilac: '#c7bbdf',
  mint: '#a6beb0', leaf: '#648b66', leafLight: '#8ca679', cream: '#f4dbc0', ink: '#51474b',
};

export type AvatarConfig = Partial<AvatarAppearance>;
export type Hotspot = { kind: 'gifts' | 'piggy' | 'cake' | 'board'; position: THREE.Vector3; radius: number; object: THREE.Object3D };

function littlePlant(b: Blocks, x: number, z: number, baseY: number, scale = 1, pot = P.peach) {
  if (!b.plants) return;
  b.box(x, baseY + 0.13 * scale, z, 0.34 * scale, 0.26 * scale, 0.34 * scale, pot);
  b.box(x, baseY + 0.25 * scale, z, 0.4 * scale, 0.06 * scale, 0.4 * scale, pot);
  b.box(x, baseY + 0.285 * scale, z, 0.3 * scale, 0.035 * scale, 0.3 * scale, '#7d644e');
  b.box(x, baseY + 0.53 * scale, z, 0.065 * scale, 0.49 * scale, 0.065 * scale, P.leaf);
  b.box(x - 0.12 * scale, baseY + 0.5 * scale, z, 0.25 * scale, 0.15 * scale, 0.18 * scale, P.leaf, 0, 0, -0.28);
  b.box(x + 0.1 * scale, baseY + 0.65 * scale, z + 0.02 * scale, 0.24 * scale, 0.17 * scale, 0.2 * scale, P.leafLight, 0, 0, 0.3);
  b.box(x - 0.045 * scale, baseY + 0.8 * scale, z, 0.18 * scale, 0.19 * scale, 0.16 * scale, P.leaf);
}

function tallPlant(b: Blocks, x: number, z: number) {
  if (!b.plants) return;
  littlePlant(b, x, z, 0, 1.8, P.ivory);
  b.box(x - 0.23, 1.16, z - 0.05, 0.09, 0.65, 0.09, P.leaf, 0, 0, 0.23);
  b.box(x - 0.37, 1.41, z - 0.07, 0.5, 0.2, 0.28, P.leafLight, 0, -0.4, -0.3);
  b.box(x + 0.3, 1.33, z + 0.1, 0.4, 0.22, 0.3, P.leaf, 0, 0.45, 0.3);
  b.box(x + 0.1, 1.62, z - 0.09, 0.4, 0.24, 0.28, P.leafLight, 0, -0.3, 0.1);
}

function gift(b: Blocks, x: number, y: number, z: number, size: number, color: string, ribbon: string, height = size) {
  b.box(x, y + height / 2, z, size, height, size * 0.88, color);
  b.box(x, y + height + 0.018, z, size + 0.065, 0.07, size * 0.88 + 0.065, color);
  b.box(x, y + height / 2 + 0.012, z + size * 0.44 + 0.006, size * 0.13, height + 0.075, 0.026, ribbon);
  b.box(x, y + height + 0.059, z, size * 0.14, 0.025, size * 0.92, ribbon);
  b.box(x, y + height + 0.06, z, size * 1.065, 0.027, size * 0.12, ribbon);
  b.box(x - size * 0.115, y + height + 0.14, z, size * 0.25, 0.09, size * 0.15, ribbon, 0, 0, -0.28);
  b.box(x + size * 0.115, y + height + 0.14, z, size * 0.25, 0.09, size * 0.15, ribbon, 0, 0, 0.28);
}

function textPlane(text: string, width: number, height: number, color: string, font = '900 104px Georgia') {
  const group = new THREE.Group();
  if (typeof document === 'undefined') return group;
  const canvas = document.createElement('canvas');
  canvas.width = 768;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');
  if (!ctx) return group;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = font;
  const lines = text.split('\n');
  lines.forEach((line, i) => ctx.fillText(line, 384, 256 + (i - (lines.length - 1) / 2) * 118, 710));
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false, side: THREE.DoubleSide });
  const plane = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
  group.add(plane);
  return group;
}

function createWishBoard(): {group:THREE.Group; updateWishes:(wishes:Wish[])=>void} {
  const group = new THREE.Group();
  group.name='The birthday wish board';
  new Blocks()
    .box(0,1.89,0,1.8,1.45,.14,'#bc936e')
    .box(0,1.89,.084,1.64,1.29,.04,'#f7e8d4')
    .box(-.66,.62,-.02,.09,1.24,.1,'#b48663')
    .box(.66,.62,-.02,.09,1.24,.1,'#b48663')
    .box(-.66,.06,.03,.35,.12,.57,'#b48663')
    .box(.66,.06,.03,.35,.12,.57,'#b48663')
    .build(group);
  if(typeof document==='undefined') return {group,updateWishes:()=>{}};
  const canvas=document.createElement('canvas');canvas.width=1024;canvas.height=768;
  const context=canvas.getContext('2d');
  if(!context)return {group,updateWishes:()=>{}};
  const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;
  const material=new THREE.MeshBasicMaterial({map:texture,transparent:false,side:THREE.DoubleSide});
  const surface=new THREE.Mesh(new THREE.PlaneGeometry(1.59,1.2),material);surface.position.set(0,1.9,.11);group.add(surface);
  const updateWishes=(wishes:Wish[])=>{
    const ctx=context;
    ctx.fillStyle='#f7e8d4';ctx.fillRect(0,0,1024,768);
    ctx.fillStyle='#765742';ctx.textAlign='center';ctx.font='700 66px Georgia';ctx.fillText('Birthday wishes',512,90);
    const recent=wishes.filter(w=>w.status==='approved').slice(-3).reverse();
    if(!recent.length){ctx.fillStyle='#a88970';ctx.font='38px Georgia';ctx.fillText('A little note. A lovely memory.',512,330);ctx.fillText('Tap to leave your birthday wish',512,396);}
    recent.forEach((wish,i)=>{
      const y=148+i*174;ctx.fillStyle=['#fffaf1','#efe8f5','#f7e2db'][i];ctx.fillRect(48,y,928,153);
      ctx.fillStyle='#927061';ctx.fillRect(480,y-9,65,19);ctx.textAlign='left';ctx.fillStyle='#644c42';ctx.font='700 30px sans-serif';ctx.fillText(wish.name,77,y+45,870);
      ctx.font='29px sans-serif';ctx.fillStyle='#856e62';
      const words=wish.text.split(/\s+/);let line='',lineNo=0;
      for(const word of words){const trial=line?`${line} ${word}`:word;if(ctx.measureText(trial).width>845&&line){ctx.fillText(line,77,y+88+lineNo*35);lineNo++;line=word;if(lineNo===2)break;}else line=trial;}
      if(lineNo<2)ctx.fillText(line,77,y+88+lineNo*35,845);
    });
    texture.needsUpdate=true;
  };
  updateWishes([]);
  return {group,updateWishes};
}

/** A complete open-front dollhouse. All coordinates are in world units. */
export function createApartment(config: PublicPartyConfig): {
  group: THREE.Group;
  hotspots: Hotspot[];
  obstacles: { x: number; z: number; w: number; d: number }[];
  cake: THREE.Group;
  flame: THREE.Group;
  updateWishes: (wishes: Wish[]) => void;
  animate: (time: number) => void;
} {
  const group = new THREE.Group();
  group.name = 'The birthday apartment';
  const b = new Blocks();
  b.plants = config.decorations.plants;
  const lights = new Blocks();
  lights.section = 'architecture';
  b.section = 'architecture';

  // A substantial little architectural model, with individually laid oak planks.
  b.box(0, -0.25, 0, 12.45, 0.45, 9.45, '#debfaa');
  b.box(0, -0.49, 0, 12.14, 0.08, 9.14, '#c8a58d');
  const planks = ['#ead1b5', '#e6caae', '#edd6bd', '#e8cdb1', '#e3c6a7', '#e9cfb6'];
  for (let row = 0; row < 6; row++) {
    for (let col = 0; col < 16; col++) {
      b.box(-5.625 + col * 0.75, -0.026, -3.75 + row * 1.5, 0.741, 0.065, 1.49, planks[(col * 7 + row * 3) % planks.length]);
    }
  }
  b.box(0, 1.7, -4.58, 12.32, 3.4, 0.2, P.wall);
  b.box(-6.08, 1.7, 0, 0.2, 3.4, 9.3, '#e9dace');
  b.box(0, 3.43, -4.57, 12.38, 0.11, 0.26, P.trim);
  b.box(-6.07, 3.43, 0, 0.26, 0.11, 9.31, P.trim);
  b.box(0, 0.14, -4.425, 12, 0.23, 0.065, P.trim);
  b.box(-5.935, 0.14, 0, 0.065, 0.23, 9, P.trim);

  // A sunlit window, clouds and generous linen curtains.
  b.box(0.25, 2.05, -4.431, 3.05, 1.91, 0.045, '#f9e2cb');
  lights.box(0.25, 2.07, -4.388, 2.75, 1.65, 0.055, '#b7dce0');
  lights.box(-0.51, 2.42, -4.345, 0.49, 0.12, 0.018, '#fff8e8');
  lights.box(-0.4, 2.49, -4.342, 0.25, 0.1, 0.019, '#fff8e8');
  lights.box(0.88, 1.82, -4.344, 0.5, 0.11, 0.018, '#fff8e8');
  lights.box(1.01, 1.9, -4.341, 0.23, 0.11, 0.019, '#fff8e8');
  for (const x of [-1.21, 0.25, 1.71]) b.box(x, 2.08, -4.28, 0.085, 1.88, 0.12, P.trim);
  for (const y of [1.17, 2.04, 3.0]) b.box(0.25, y, -4.28, 3.05, 0.09, 0.12, P.trim);
  b.box(0.25, 1.12, -4.2, 3.25, 0.12, 0.39, P.trim);
  b.box(0.25, 3.16, -4.12, 3.9, 0.045, 0.045, P.wood);
  for (const side of [-1, 1]) {
    for (let fold = 0; fold < 4; fold++) {
      const x = 0.25 + side * (1.48 + fold * 0.13);
      b.box(x, 2.15, -4.04 + (fold % 2) * 0.035, 0.16, 1.91, 0.12, fold % 2 ? '#e7cbbb' : '#f5e0d0');
    }
  }
  littlePlant(b, -0.74, -4.0, 1.18, 0.56, P.peach);
  b.box(1.05, 1.22, -4.12, 0.36, 0.08, 0.24, P.lavender);
  b.box(1.08, 1.29, -4.12, 0.31, 0.06, 0.22, P.cream);

  b.section = 'kitchen';
  // Kitchen: mint joinery, oak counter, pink fridge, tiny coffee station.
  b.box(-5.29, 0.9, -3.83, 0.91, 1.8, 1.07, '#dec5c5');
  b.box(-5.29, 1.27, -3.255, 0.83, 0.93, 0.08, '#ecd7d4');
  b.box(-5.29, 0.42, -3.255, 0.83, 0.66, 0.08, '#ecd7d4');
  b.box(-4.975, 1.14, -3.19, 0.045, 0.36, 0.045, P.wood);
  b.box(-4.975, 0.58, -3.19, 0.045, 0.23, 0.045, P.wood);
  b.box(-5.43, 1.5, -3.195, 0.2, 0.19, 0.008, P.cream, 0, 0, 0.12);
  b.box(-5.22, 1.34, -3.193, 0.14, 0.13, 0.009, P.lavender, 0, 0, -0.18);
  b.box(-3.48, 0.49, -3.84, 2.52, 0.97, 1.05, P.mint);
  b.box(-3.48, 1.0, -3.8, 2.67, 0.11, 1.2, '#f8ead7');
  b.box(-3.48, 0.065, -3.4, 2.45, 0.13, 0.06, '#759486');
  for (let i = 0; i < 3; i++) {
    const x = -4.32 + i * 0.83;
    b.box(x, 0.56, -3.286, 0.78, 0.78, 0.035, i === 1 ? '#9ab1a2' : '#abc2b1');
    b.box(x, 0.79, -3.25, 0.25, 0.035, 0.045, P.wood);
  }
  b.box(-4.22, 1.07, -3.73, 0.64, 0.018, 0.63, '#c2c4b9');
  b.box(-4.22, 1.08, -3.73, 0.5, 0.015, 0.47, '#94a7a4');
  b.box(-4.22, 1.23, -4.11, 0.045, 0.32, 0.045, '#dac09d');
  b.box(-4.22, 1.4, -4.015, 0.045, 0.045, 0.23, '#dac09d');
  b.box(-4.22, 1.345, -3.91, 0.045, 0.12, 0.045, '#dac09d');
  b.box(-3.07, 1.066, -3.72, 0.58, 0.024, 0.6, '#5c615e');
  for (const x of [-3.22, -2.93]) for (const z of [-3.88, -3.56]) b.box(x, 1.082, z, 0.18, 0.009, 0.18, '#333f3b');
  b.box(-2.49, 1.23, -4.01, 0.28, 0.33, 0.3, P.ivory);
  b.box(-2.49, 1.17, -3.849, 0.16, 0.16, 0.025, '#7b706b');
  b.box(-2.49, 1.37, -4.005, 0.3, 0.06, 0.32, P.wood);
  b.box(-3.55, 2.05, -4.29, 2.42, 0.095, 0.34, P.wood);
  for (let i = 0; i < 5; i++) {
    b.box(-4.43 + i * 0.14, 2.31, -4.27, 0.11, 0.44 + (i % 2) * 0.05, 0.2, [P.pink, P.ivory, P.lavender, P.peach, P.mint][i]);
  }
  littlePlant(b, -2.77, -4.21, 2.1, 0.58, P.cream);
  b.box(-3.47, 2.27, -4.26, 0.25, 0.34, 0.25, '#eee0d2');
  b.box(-3.47, 2.46, -4.26, 0.28, 0.045, 0.28, P.wood);

  b.section = 'sofa';
  // Lavender couch, two generous cushions, a folded throw and little feet.
  for (const x of [-5.47, -3.12]) for (const z of [-0.57, 0.47]) b.box(x, 0.16, z, 0.16, 0.29, 0.16, P.wood);
  b.box(-4.3, 0.42, -0.02, 3.05, 0.37, 1.51, '#9c91bb');
  b.box(-4.3, 0.89, -0.61, 3.08, 0.75, 0.36, P.lavender);
  for (const x of [-5.7, -2.9]) b.box(x, 0.68, -0.015, 0.3, 0.7, 1.47, P.lilac);
  for (const x of [-4.96, -3.65]) {
    b.box(x, 0.66, 0.02, 1.22, 0.22, 1.13, '#bcb0d9');
    b.box(x, 0.98, -0.36, 1.22, 0.57, 0.23, '#c4b9df', -0.11);
  }
  b.box(-5.22, 0.99, -0.09, 0.51, 0.45, 0.2, '#efd9bb', -0.12, -0.12, 0.13);
  b.box(-3.31, 0.94, -0.05, 0.49, 0.42, 0.22, '#efb4b3', -0.17, 0.11, -0.15);
  b.box(-4.43, 0.79, 0.36, 0.49, 0.028, 0.62, '#f5dfc6');
  b.box(-4.43, 0.54, 0.684, 0.49, 0.47, 0.035, '#f5dfc6');
  for (let i = 0; i < 4; i++) b.box(-4.6 + i * 0.11, 0.29, 0.687, 0.03, 0.08, 0.035, '#ead1b7');
  b.section = 'side-table';
  b.box(-2.06, 0.59, -0.29, 0.89, 0.105, 0.85, P.wood);
  for (const x of [-2.38, -1.74]) for (const z of [-0.6, 0.02]) b.box(x, 0.29, z, 0.075, 0.57, 0.075, P.wood);
  b.box(-2.11, 0.682, -0.14, 0.4, 0.08, 0.27, P.pink, 0, -0.16);
  b.box(-2.12, 0.744, -0.14, 0.35, 0.045, 0.25, P.ivory, 0, -0.16);
  littlePlant(b, -1.99, -0.48, 0.645, 0.47, P.ivory);

  b.section = 'rug';
  // A tactile layered rug. Its open centre is the guests' dancing space.
  b.box(0.1, 0.015, 1.6, 5.45, 0.035, 3.55, '#f4e4d3');
  b.box(0.1, 0.037, 1.6, 5.14, 0.014, 3.24, '#e9bda8');
  b.box(0.1, 0.047, 1.6, 4.91, 0.01, 3.0, '#f1ccbb');
  for (const z of [0.2, 3.0]) b.box(0.1, 0.058, z, 4.8, 0.005, 0.06, '#dca691');
  for (let i = 0; i < 22; i++) {
    for (const side of [-1, 1]) b.box(-2.48 + i * 0.247, 0.018, 1.6 + side * 1.86, 0.08, 0.025, 0.23, '#f4e4d3');
  }

  b.section = 'architecture';
  // Framed voxel rainbow on the left wall.
  b.box(-5.91, 2.24, -0.98, 0.12, 1.32, 1.15, P.wood);
  b.box(-5.836, 2.24, -0.98, 0.025, 1.14, 0.97, '#fff4e3');
  const rainbow = [P.orange, P.pink, P.lavender];
  rainbow.forEach((color, i) => {
    const w = 0.7 - i * 0.18;
    b.box(-5.816, 2.24, -0.98 - w / 2, 0.025, 0.44 - i * 0.055, 0.07, color);
    b.box(-5.816, 2.24, -0.98 + w / 2, 0.025, 0.44 - i * 0.055, 0.07, color);
    b.box(-5.816, 2.49 - i * 0.09, -0.98, 0.025, 0.07, w, color);
  });
  b.box(-5.72, 1.83, 1.55, 0.49, 0.09, 2.11, P.wood);
  for (let i = 0; i < 6; i++) b.box(-5.7, 2.055 + (i % 2) * 0.02, 0.83 + i * 0.15, 0.28, 0.37 + (i % 2) * 0.04, 0.11, [P.lilac, P.ivory, P.pink, P.mint, P.orange, P.cream][i]);
  littlePlant(b, -5.66, 2.12, 1.88, 0.7, P.peach);

  b.section = 'cake-table';
  // Celebration table with a separate, independently animated cake.
  b.box(0, 0.62, -1.18, 1.92, 0.12, 1.63, '#f7e8d7');
  b.box(0, 0.335, -1.18, 0.68, 0.56, 0.63, P.wood);
  b.box(0, 0.065, -1.18, 1.25, 0.12, 1.12, '#dab894');
  const cake = new THREE.Group();
  cake.name = 'Birthday cake';
  cake.position.set(0, 0.71, -4.12);
  cake.scale.set(.75, .88, .75);
  const cakeBlocks = new Blocks();
  cakeBlocks.box(0, 0.035, 0, 1.45, 0.065, 1.36, P.mint);
  cakeBlocks.box(0, 0.095, 0, 1.31, 0.065, 1.23, P.ivory);
  cakeBlocks.box(0, 0.305, 0, 1.17, 0.38, 1.1, '#f4bfbd');
  cakeBlocks.box(0, 0.49, 0, 1.2, 0.1, 1.13, '#fff2da');
  cakeBlocks.box(0, 0.67, 0, 0.8, 0.31, 0.77, '#fff2da');
  cakeBlocks.box(0, 0.834, 0, 0.83, 0.065, 0.8, '#e7acbd');
  for (let i = 0; i < 6; i++) {
    const x = -0.5 + i * 0.2;
    cakeBlocks.box(x, 0.41 - (i % 2) * 0.035, 0.56, 0.11, 0.12 + (i % 2) * 0.07, 0.035, '#fff2da');
    cakeBlocks.box(x, 0.25 + (i % 2) * 0.055, 0.558, 0.035, 0.07, 0.028, [P.lavender, P.orange, P.mint][i % 3], 0, 0, i % 2 ? 0.35 : -0.35);
    cakeBlocks.box(0.588, 0.25 + (i % 2) * 0.045, -0.47 + i * 0.19, 0.018, 0.06, 0.035, [P.mint, P.lavender, P.orange][i % 3], 0.3);
  }
  for (const x of [-0.32, 0.32]) for (const z of [-0.28, 0.28]) cakeBlocks.box(x, 0.9, z, 0.11, 0.11, 0.11, '#d9878c');
  // A proper pixel-art number-two candle.
  const digits: Record<string,string[]> = {'0':['111','101','101','101','111'],'1':['010','110','010','010','111'],'2':['111','001','111','100','111'],'3':['111','001','111','001','111'],'4':['101','101','111','001','001'],'5':['111','100','111','001','111'],'6':['111','100','111','101','111'],'7':['111','001','010','010','010'],'8':['111','101','111','101','111'],'9':['111','101','111','001','111']};
  const age = String(config.child.age);
  [...age].forEach((digit, index) => (digits[digit] || digits['2']).forEach((row, y) => [...row].forEach((pixel, x) => {
    if (pixel === '1') cakeBlocks.box((x - 1 + (index - (age.length - 1)/2) * 4) * .087, 1.4-y*.095, 0, .09,.10,.095,config.theme.accent);
  })));
  cakeBlocks.box(0, 0.93, 0, 0.045, 0.19, 0.045, P.ivory);
  cakeBlocks.build(cake);
  const flame = new THREE.Group();
  flame.position.set(0, 1.56, 0);
  new Blocks().box(0, 0, 0, 0.09, 0.14, 0.08, '#ffcf69').box(0, 0.06, 0, 0.052, 0.08, 0.047, '#fff2bc').build(flame, glow, false);
  cake.add(flame);
  group.add(cake);

  b.section = 'gift-table';
  // Gift table and pink piggy are individually raycastable, clearly separated.
  b.box(3.91, 0.94, -2.97, 3.35, 0.13, 1.59, '#f7e7d4');
  b.box(3.91, 0.82, -2.97, 3.13, 0.15, 1.4, '#d6aa83');
  for (const x of [2.5, 5.32]) for (const z of [-3.52, -2.43]) b.box(x, 0.42, z, 0.13, 0.85, 0.13, P.wood);
  b.box(3.13, 1.016, -2.97, 1.1, 0.018, 1.6, '#ecd0ce');
  b.box(3.13, 0.87, -2.16, 1.1, 0.3, 0.02, '#ecd0ce');
  const gifts = new THREE.Group();
  gifts.position.set(3.835, 1.024, -3.98);
  gifts.scale.set(.96,1,.8);
  gifts.name = 'A table full of birthday wishes';
  const gb = new Blocks();
  gift(gb, -0.54, 0, -0.12, 0.67, P.lavender, '#e9dcfa', 0.69);
  gift(gb, 0.22, 0, 0.15, 0.7, P.peach, '#fff1d7', 0.49);
  gift(gb, 0.2, 0.595, 0.04, 0.43, P.mint, '#e0eedc', 0.35);
  gift(gb, -0.49, 0, 0.55, 0.36, '#eaba9f', '#fff4df', 0.28);
  gb.build(gifts);
  group.add(gifts);
  const piggy = new THREE.Group();
  piggy.name = 'Little birthday piggy bank';
  piggy.position.set(5.381, 1.026, -3.804);
  piggy.scale.set(.96,1,.8);
  const pb = new Blocks();
  pb.box(0, 0.36, 0, 0.7, 0.52, 0.58, '#eca8b4');
  pb.box(0, 0.36, 0.02, 0.77, 0.34, 0.51, '#f1b4bf');
  pb.box(0, 0.39, 0.31, 0.4, 0.34, 0.17, '#f4bfca');
  pb.box(0, 0.31, 0.43, 0.28, 0.17, 0.13, '#df91a3');
  for (const x of [-0.062, 0.062]) pb.box(x, 0.322, 0.501, 0.035, 0.048, 0.018, '#b86782');
  for (const x of [-0.245, 0.245]) {
    pb.box(x, 0.11, -0.15, 0.15, 0.2, 0.14, '#da90a3');
    pb.box(x, 0.11, 0.18, 0.15, 0.2, 0.14, '#da90a3');
    pb.box(x, 0.64, 0.16, 0.18, 0.24, 0.12, '#eea7b6', 0, 0, x < 0 ? -0.22 : 0.22);
    pb.box(x, 0.65, 0.225, 0.088, 0.12, 0.016, '#cf7d99');
  }
  for (const x of [-0.151, 0.151]) {
    pb.box(x, 0.452, 0.407, 0.06, 0.068, 0.018, '#684958');
    pb.box(x - 0.013, 0.47, 0.419, 0.018, 0.018, 0.01, P.ivory);
  }
  pb.box(0, 0.63, -0.07, 0.3, 0.015, 0.045, '#a5677b');
  pb.box(0.04, 0.72, -0.07, 0.13, 0.18, 0.035, '#dfb65c', 0, 0, -0.13);
  pb.box(0.4, 0.41, -0.12, 0.16, 0.055, 0.055, '#dc93a7');
  pb.box(0.46, 0.46, -0.12, 0.055, 0.14, 0.055, '#dc93a7');
  pb.box(0.425, 0.515, -0.12, 0.115, 0.055, 0.055, '#dc93a7');
  pb.build(piggy);
  group.add(piggy);

  const wallGreeting = textPlane(`${config.child.age} \n${config.child.name || 'sweet'}`, 2.0, 1.35, config.theme.accent, 'italic 900 108px Georgia');
  wallGreeting.position.set(4.0, 2.69, -4.895);
  group.add(wallGreeting);
  const tableLabel = textPlane('little wishes', 1.17, 0.4, '#8b6958', '700 82px Georgia');
  tableLabel.position.set(3.69, 0.883, -3.315);
  group.add(tableLabel);

  b.section = 'architecture';
  // Pixel bunting drapes across the room's rear wall.
  const pennants = [P.pink, P.lavender, P.peach, P.mint, '#e9c477'];
  for (let i = 0; i < (config.decorations.bunting ? 15 : 0); i++) {
    const x = -5.64 + i * 0.795;
    const y = 3.19 - Math.sin((i / 14) * Math.PI) * 0.28;
    const nextY = 3.19 - Math.sin(((i + 1) / 14) * Math.PI) * 0.28;
    if (i < 14) b.box(x + 0.3975, (y + nextY) / 2, -3.98, 0.8, 0.016, 0.017, '#b19079', 0, 0, Math.atan2(nextY - y, 0.795));
    b.box(x, y - 0.125, -3.97, 0.37, 0.24, 0.024, pennants[i % pennants.length]);
    b.box(x, y - 0.27, -3.97, 0.26, 0.07, 0.024, pennants[i % pennants.length]);
    b.box(x, y - 0.327, -3.97, 0.14, 0.055, 0.024, pennants[i % pennants.length]);
  }

  b.section = 'plants';
  tallPlant(b, 5.34, 2.81);
  tallPlant(b, -5.27, 3.44);
  b.section = 'sofa';
  // A tiny voxel dachshund plush watches the dance floor from the couch.
  b.box(-4.04, 0.9, 0.28, 0.5, 0.24, 0.22, '#c69269');
  b.box(-3.78, 1.05, 0.29, 0.24, 0.3, 0.23, '#dba77e');
  b.box(-3.77, 0.99, 0.43, 0.18, 0.15, 0.12, '#b67b55');
  b.box(-3.84, 1.07, 0.416, 0.034, 0.04, 0.02, P.ink);
  b.box(-3.91, 1.02, 0.29, 0.08, 0.26, 0.25, '#b67b55');
  b.box(-4.3, 0.99, 0.27, 0.14, 0.06, 0.06, '#c69269', 0, 0, -0.35);

  b.section = 'train';
  // A wooden toy train and alphabet blocks, all deliberately larger than life.
  const toyColors = [P.orange, P.mint, P.lavender];
  for (let i = 0; i < 3; i++) {
    const x = 3.02 + i * 0.61;
    b.box(x, 0.16, 2.61, 0.51, 0.16, 0.3, toyColors[i]);
    b.box(x, 0.29, 2.61, i === 0 ? 0.24 : 0.41, i === 0 ? 0.3 : 0.16, 0.28, toyColors[i]);
    for (const wx of [-0.17, 0.17]) for (const wz of [-0.17, 0.17]) b.box(x + wx, 0.095, 2.61 + wz, 0.12, 0.12, 0.06, P.darkWood);
    if (i > 0) b.box(x - 0.31, 0.13, 2.61, 0.18, 0.06, 0.07, P.wood);
  }
  b.box(2.9, 0.43, 2.61, 0.07, 0.15, 0.07, P.darkWood);
  b.section = 'blocks';
  b.box(3.82, 0.17, 1.53, 0.33, 0.33, 0.33, P.pink, 0, 0.21);
  b.box(4.23, 0.15, 1.47, 0.3, 0.3, 0.3, P.mint, 0, -0.16);
  b.box(4.18, 0.44, 1.48, 0.26, 0.27, 0.26, P.cream, 0, 0.06);

  // Five little cubic balloons catch light and bob independently.
  const floaters: { object: THREE.Group; y: number; phase: number }[] = [];
  const balloonSpecs = [
    { x: 5.52, y: 2.59, z: -.7, c: P.pink },
    { x: 5.02, y: 2.92, z: -.95, c: P.lilac },
    { x: 5.89, y: 3.01, z: -1.15, c: P.peach },
    { x: -5.08, y: 2.47, z: 1.43, c: P.peach },
    { x: -5.58, y: 2.82, z: 1.24, c: P.mint },
  ];
  (config.decorations.balloons ? balloonSpecs : []).forEach((s, i) => {
    // Stable pseudo-random decorative colors so every client sees the same palette.
    const palette=config.abilities.balloonColors;
    const color=palette[(i*7+config.child.name.length*3)%palette.length];
    const balloon = new THREE.Group();
    balloon.position.set(s.x, s.y, s.z);
    const bb = new Blocks();
    bb.box(0, 0, 0, 0.47, 0.57, 0.47, color);
    bb.box(0, 0.018, 0, 0.57, 0.39, 0.43, color);
    bb.box(0, 0.018, 0, 0.43, 0.39, 0.57, color);
    bb.box(0, 0.31, 0, 0.3, 0.075, 0.3, color);
    bb.box(0, -0.313, 0, 0.09, 0.09, 0.09, color);
    bb.box(-0.15, 0.135, 0.241, 0.085, 0.18, 0.008, '#fff2e7');
    bb.box(0, -1.15, 0, 0.014, 1.64, 0.014, '#bcaa95');
    bb.build(balloon);
    group.add(balloon);
    floaters.push({ object: balloon, y: s.y, phase: i * 1.7 });
  });
  b.section = 'weights';
  b.box(5.34, 0.12, -1.3, 0.27, 0.24, 0.27, P.lavender);
  b.box(-3.61, 0.12, 1.68, 0.27, 0.24, 0.27, P.pink);
  b.build(group);
  lights.build(group, glow, false);

  const birthdaySign=textPlane(`${config.child.name}\nturns ${config.child.age}!`,4.1,1.26,config.theme.accent,'700 100px Georgia');
  birthdaySign.name='Birthday child name sign';birthdaySign.userData.childName=config.child.name;
  birthdaySign.position.set(.1,3.18,-4.77);group.add(birthdaySign);

  const hotspots: Hotspot[] = [
    { kind: 'gifts', position: new THREE.Vector3(3.84, 1.7, -3.78), radius: 0.95, object: gifts },
    { kind: 'piggy', position: new THREE.Vector3(5.38, 1.58, -3.67), radius: 0.6, object: piggy },
    { kind: 'cake', position: new THREE.Vector3(0, 1.53, -4.12), radius: 0.85, object: cake },
  ];
  hotspots.forEach((spot) => spot.object.traverse((object) => { object.userData.hotspot = spot.kind; }));
  const obstacles = ROOM_OBSTACLES;
  const board = createWishBoard();
  board.group.position.set(5.3,0,.32);
  group.add(board.group);
  hotspots.push({ kind:'board', position: new THREE.Vector3(5.3,2.3,.32), radius:1, object:board.group });
  board.group.traverse(object => {object.userData.hotspot='board'});
  return {
    group, hotspots, obstacles, cake, flame, updateWishes: board.updateWishes,
    animate(time) {
      floaters.forEach(({ object, y, phase }) => {
        object.position.y = y + Math.sin(time * 1.2 + phase) * 0.055;
        object.rotation.z = Math.sin(time * 0.72 + phase) * 0.018;
      });
      flame.scale.setScalar(0.95 + Math.sin(time * 9) * 0.045 + Math.sin(time * 15) * 0.025);
    },
  };
}

/** Cute cubic guests, +Z forward. Walking motion never changes world position. */
export function createAvatar(config: Partial<AvatarConfig> = {}): {
  group: THREE.Group;
  animate: (time: number, moving: boolean, celebrating?: boolean) => void;
} {
  const skin = config.skin || '#eac29f';
  const garment = config.outfitStyle ?? (config.presentation==='boy'?'overalls':'dress');
  const bottom = config.bottom ?? '#7298bd', shoes = config.shoes ?? '#fff3df';
  const hair = config.hair || '#76523e';
  const outfit = config.outfit || '#b6abd8';
  const hairstyle = (config.hairstyle || 'short').toLowerCase();
  const accessory = (config.accessory || 'none').toLowerCase();
  const group = new THREE.Group();
  group.name = 'Party guest';
  const body = new THREE.Group();
  body.name='body';
  group.add(body);
  const b = new Blocks();
  b.box(0, 0.56, 0, 0.39, 0.41, 0.27, outfit);
  b.box(0, 0.78, 0, 0.15, 0.085, 0.16, skin);
  b.box(0, 0.717, 0.147, 0.14, 0.055, 0.018, '#fff1de');
  b.box(0, 0.372, 0, 0.39, 0.07, 0.28, outfit);
  if (garment === 'dress' || garment === 'skirt') {
    b.box(0,.425,0,.43,.16,.31,garment==='skirt'?bottom:outfit);
    b.box(0,.355,0,.5,.075,.35,garment==='skirt'?bottom:outfit);
    b.box(0,.665,.157,.075,.035,.024,'#fff1de');
  } else if(garment==='overalls') {
    b.box(-.118,.62,.15,.055,.21,.025,'#fff1de');
    b.box(.118,.62,.15,.055,.21,.025,'#fff1de');
    b.box(0,.54,.155,.25,.20,.026,bottom);
    for(const x of[-.118,.118])b.box(x,.68,.17,.035,.035,.025,'#f4d689');
  } else {
    b.box(0,.41,.15,.39,.05,.025,bottom);
    b.box(0,.68,.151,.055,.09,.026,'#fff3df');
  }
  if(config.presentation==='boy'){b.box(-.205,.7,0,.075,.12,.27,outfit);b.box(.205,.7,0,.075,.12,.27,outfit);}else{b.box(0,.575,.155,.048,.048,.02,'#fff1de');}
  const torsoBlockCount = b.items.length;
  b.box(0, 1.006, 0.012, 0.48, 0.45, 0.43, skin);
  b.box(0, 1.252, -0.015, 0.51, 0.075, 0.45, hair);
  b.box(0, 1.09, -0.216, 0.5, 0.32, 0.045, hair);
  // Friendly eyes, a little button nose, rosy cheeks and a tiny smile.
  for (const x of [-0.108, 0.108]) {
    b.box(x, 1.035, 0.238, 0.055, 0.065, 0.018, '#493a3a');
    b.box(x - 0.012, 1.055, 0.25, 0.017, 0.019, 0.007, '#fff6e6');
    b.box(x * 1.4, 0.959, 0.237, 0.075, 0.035, 0.012, '#df9d92');
  }
  b.box(0, 0.995, 0.249, 0.035, 0.044, 0.04, skin);
  b.box(0, 0.925, 0.238, 0.059, 0.022, 0.018, '#aa736a');
  b.box(-0.04, 0.942, 0.237, 0.025, 0.023, 0.017, '#aa736a');
  b.box(0.04, 0.942, 0.237, 0.025, 0.023, 0.017, '#aa736a');
  for (const x of [-0.263, 0.263]) b.box(x, 1.01, 0.012, 0.06, 0.12, 0.11, skin);

  if (hairstyle === 'pigtails' || hairstyle === 'ponytail' || hairstyle === 'bun') {
    b.box(-.04,1.202,.22,.42,.12,.06,hair);
    if(hairstyle==='pigtails')for(const x of[-.33,.33]){b.box(x,1.08,-.06,.17,.31,.24,hair);b.box(x,1.18,.065,.18,.07,.055,outfit);b.box(x*1.1,.9,-.06,.14,.1,.2,hair);}
    else if(hairstyle==='ponytail'){b.box(0,1.09,-.31,.24,.4,.16,hair);b.box(0,1.23,-.27,.27,.06,.15,outfit);}
    else{b.box(0,1.37,-.12,.3,.2,.29,hair);b.box(0,1.46,-.12,.22,.07,.23,hair);}
  } else if(hairstyle==='mohawk'){
    for(let i=0;i<4;i++)b.box(0,1.34+(i%2)*.055,-.15+i*.105,.13,.2,.13,hair);
  } else if (hairstyle === 'bob') {
    for (const x of [-0.257, 0.257]) b.box(x, 1.065, -0.005, 0.088, 0.38, 0.44, hair);
    b.box(-0.065, 1.199, 0.225, 0.35, 0.11, 0.055, hair);
  } else if (hairstyle === 'long') {
    for (const x of [-0.26, 0.26]) b.box(x, 0.99, -0.065, 0.095, 0.57, 0.34, hair);
    b.box(0, 0.93, -0.231, 0.51, 0.58, 0.095, hair);
    b.box(-0.11, 1.194, 0.225, 0.28, 0.14, 0.06, hair);
    b.box(0.158, 1.202, 0.225, 0.18, 0.12, 0.06, hair);
  } else if (hairstyle === 'curls' || hairstyle === 'curly') {
    for (let x = -1; x <= 1; x++) for (let z = -1; z <= 1; z++) {
      b.box(x * 0.18, 1.29 + ((x + z) % 2) * 0.022, z * 0.16, 0.21, 0.17, 0.21, hair);
    }
    for (const x of [-0.275, 0.275]) {
      b.box(x, 1.175, 0.075, 0.13, 0.2, 0.23, hair);
      b.box(x, 1.12, -0.125, 0.12, 0.19, 0.18, hair);
    }
    b.box(-0.115, 1.204, 0.216, 0.17, 0.12, 0.11, hair);
    b.box(0.105, 1.216, 0.223, 0.17, 0.1, 0.11, hair);
  } else {
    b.box(-0.097, 1.206, 0.228, 0.33, 0.11, 0.06, hair);
    b.box(-0.236, 1.133, -0.029, 0.045, 0.18, 0.33, hair);
    b.box(0.236, 1.166, -0.06, 0.045, 0.15, 0.27, hair);
    b.box(-0.08, 1.313, -0.012, 0.3, 0.07, 0.32, hair);
  }

  if(accessory==='glasses'){
    for(const x of[-.11,.11]){b.box(x,1.034,.265,.17,.025,.025,'#493a3a');b.box(x,.979,.265,.17,.025,.025,'#493a3a');for(const d of[-.073,.073])b.box(x+d,1.005,.265,.025,.07,.025,'#493a3a');}
    b.box(0,1.019,.273,.06,.024,.03,'#493a3a');
  } else if(accessory==='cap'){
    b.box(0,1.286,0,.55,.115,.48,outfit);b.box(0,1.25,.30,.49,.05,.24,outfit);b.box(0,1.35,0,.36,.055,.32,outfit);
  } else if(accessory==='crown'){
    b.box(0,1.31,0,.54,.09,.46,'#e9bc58');for(const x of[-.2,0,.2])b.box(x,1.405,.185,.085,.14,.08,'#f4d689');b.box(0,1.327,.242,.07,.06,.025,'#e996ad');
  } else if (accessory === 'party-hat' || accessory === 'hat' || accessory === 'party') {
    b.box(0.04, 1.346, 0, 0.41, 0.075, 0.39, '#f0bf7f');
    b.box(0.04, 1.442, 0, 0.31, 0.14, 0.3, '#edab96');
    b.box(0.04, 1.555, 0, 0.22, 0.09, 0.21, '#bba7d7');
    b.box(0.04, 1.648, 0, 0.13, 0.11, 0.12, '#edab96');
    b.box(0.04, 1.733, 0, 0.095, 0.09, 0.095, '#f7daa0');
    b.box(0.035, 1.45, 0.155, 0.056, 0.052, 0.014, '#fff2d9');
  } else if (accessory === 'bow') {
    b.box(0.2, 1.266, 0.225, 0.105, 0.105, 0.068, '#e89ba5');
    b.box(0.098, 1.267, 0.215, 0.11, 0.17, 0.061, '#efadba', 0, 0, 0.18);
    b.box(0.3, 1.267, 0.215, 0.11, 0.17, 0.061, '#efadba', 0, 0, -0.18);
  } else if (accessory === 'star') {
    b.box(-0.195, 1.2, 0.247, 0.18, 0.07, 0.035, '#edc567');
    b.box(-0.195, 1.2, 0.247, 0.07, 0.19, 0.035, '#edc567');
    b.box(-0.195, 1.2, 0.255, 0.12, 0.12, 0.035, '#f4d689');
  }
  const head = new THREE.Group(); head.name='head'; head.position.y=.78;
  const headBlocks = new Blocks(); headBlocks.items=b.items.splice(torsoBlockCount).map(block=>({...block,y:block.y-.78}));
  headBlocks.build(head); body.add(head);
  b.build(body);

  const arms: THREE.Group[] = [];
  const legs: THREE.Group[] = [];
  for (const side of [-1, 1]) {
    const arm = new THREE.Group();
    arm.name = side < 0 ? 'left-arm' : 'right-arm';
    arm.position.set(side * 0.275, 0.736, 0);
    new Blocks()
      .box(0, -0.08, 0, 0.15, 0.18, 0.225, outfit)
      .box(0, -0.24, 0, 0.13, 0.19, 0.16, skin)
      .box(0, -0.328, 0.006, 0.15, 0.1, 0.18, skin)
      .build(arm);
    body.add(arm);
    arms.push(arm);
    const leg = new THREE.Group();
    leg.name = side < 0 ? 'left-leg' : 'right-leg';
    leg.position.set(side * 0.107, 0.372, 0);
    new Blocks()
      .box(0, -0.14, 0, 0.16, 0.28, 0.205, ['dress','skirt','shorts'].includes(garment) ? skin : bottom)
      .box(0,-.045,0,.168,.095,.214,garment==='shorts'?bottom:['dress','skirt'].includes(garment)?skin:bottom)
      .box(0, -0.304, 0.035, 0.18, 0.115, 0.27, shoes)
      .box(0, -0.361, 0.035, 0.187, 0.035, 0.275, '#c6b3a4')
      .build(leg);
    body.add(leg);
    legs.push(leg);
  }

  return {
    group,
    animate(time, moving, celebrating = false) {
      const stride = Math.sin(time * 9.3);
      head.rotation.y=Math.sin(time*.69)*(moving?.035:.11);
      head.rotation.x=celebrating?Math.sin(time*5)*.09:Math.sin(time*.91)*.02;
      const amount = moving ? 0.57 : 0;
      legs[0].rotation.x = stride * amount;
      legs[1].rotation.x = -stride * amount;
      arms[0].rotation.x = -stride * amount * 0.83;
      arms[1].rotation.x = stride * amount * 0.83;
      arms[0].rotation.z = celebrating ? -2.48 + Math.sin(time * 7) * 0.18 : 0.07;
      arms[1].rotation.z = celebrating ? 2.48 - Math.sin(time * 7 + 0.5) * 0.18 : -0.07;
      body.position.y = celebrating ? Math.abs(Math.sin(time * 5)) * 0.19 : moving ? Math.abs(stride) * 0.045 : Math.sin(time * 2.3) * 0.008;
      body.rotation.z = celebrating ? Math.sin(time * 5) * 0.065 : moving ? stride * 0.025 : 0;
    },
  };
}
